import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector, useStore } from "react-redux";
import i18n from "i18next";
import {
  decomposeBoxWithAideaSmartElements,
  isEligibleForAideaSmartElements,
} from "../../../../common/aideaSmartElements.es6";
import {
  setInteractiveMode,
  selectScene,
  changeBoxLayer,
  batchSetSceneFont,
} from "../../../../common/actions";
import { deriveTextFormatFromHtml } from "../../../../common/common_tools";
import { applyBrandAssetToAllScenes, brandTagOfBox } from "../../../utils/brandAssetPlacement";
import { isTextPlugin } from "../../../utils/styleClipboard";

const menuItemStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "6px 12px",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: 14,
  color: "#222",
};

const menuItemDisabledStyle = { ...menuItemStyle, cursor: "not-allowed", opacity: 0.5 };

export default function BoxContextMenu({ boxId, position, onClose }) {
  const ref = useRef(null);
  const [clamped, setClamped] = useState(null);
  const dispatch = useDispatch();
  const store = useStore();
  const toolbar = useSelector((s) => {
    const present = s.undoGroup && s.undoGroup.present ? s.undoGroup.present : s;
    return present.pluginToolbarsById && present.pluginToolbarsById[boxId];
  });
  const scenario = useSelector((s) => {
    const present = s.undoGroup && s.undoGroup.present ? s.undoGroup.present : s;
    const tb = present.pluginToolbarsById && present.pluginToolbarsById[boxId];
    return (tb && tb.state && tb.state.scenario) || null;
  });
  const processing = useSelector(
    (s) => (s.aideaSmartElementsProcessing && s.aideaSmartElementsProcessing[boxId]) || null
  );
  // Layer order (front/back) operates on the active scene's — or, when inside
  // a group, the active contained view's — `.boxes` array: the same array
  // the canvas paints from (later id = on top) and the Timeline's row
  // drag-reorder mutates via the same changeBoxLayer action.
  const scenePageId = useSelector((s) => {
    const present = s.undoGroup && s.undoGroup.present ? s.undoGroup.present : s;
    const { navItemSelected, containedViewSelected } = present;
    return containedViewSelected && containedViewSelected !== 0
      ? containedViewSelected
      : navItemSelected;
  });
  const sceneBoxes = useSelector((s) => {
    const present = s.undoGroup && s.undoGroup.present ? s.undoGroup.present : s;
    const { navItemsById, containedViewsById } = present;
    const page = containedViewsById?.[scenePageId] || navItemsById?.[scenePageId];
    return page?.boxes || [];
  });
  const layerIndex = sceneBoxes.indexOf(boxId);
  const canReorder = layerIndex !== -1 && sceneBoxes.length > 1;

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose && onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    // Capture phase: EditorBox stops mousedown propagation, so a bubbling
    // listener never heard the press on another box and two menus stayed open.
    document.addEventListener("mousedown", onDocClick, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Keep the menu inside the viewport (flip left/up near the right/bottom edge).
  useLayoutEffect(() => {
    if (!ref.current) return;
    const { width, height } = ref.current.getBoundingClientRect();
    const margin = 4;
    let x = position.x;
    let y = position.y;
    if (x + width > window.innerWidth - margin) x = Math.max(margin, x - width);
    if (y + height > window.innerHeight - margin) y = Math.max(margin, y - height);
    setClamped({ x, y });
  }, [position.x, position.y]);

  const isInteractive = toolbar && toolbar.pluginId === "InteractivePlugin";
  const canSmartElements = isEligibleForAideaSmartElements(toolbar) && !processing;
  // A brand kit asset (id tagged by the Brand panel): its current spot and
  // size can be pushed onto every other scene.
  const isBrandAsset = !!brandTagOfBox(boxId);
  // A text box's font (box state first, else what its HTML renders with) can
  // become the whole course's font — same as the canvas font button's
  // "whole course" option.
  const textFont =
    toolbar && isTextPlugin(toolbar.pluginId)
      ? (toolbar.state && toolbar.state.textFontFamily) ||
        deriveTextFormatFromHtml(toolbar.state && toolbar.state.__text).textFontFamily
      : null;
  const hasAnyItem = isInteractive || canSmartElements || canReorder || isBrandAsset || !!textFont;

  if (!hasAnyItem) return null;

  const reorder = (value) => {
    onClose && onClose();
    dispatch(changeBoxLayer(boxId, scenePageId, 0, value, sceneBoxes));
  };
  const atFront = layerIndex === sceneBoxes.length - 1;
  const atBack = layerIndex === 0;

  // Portal to <body>: the canvas has transformed ancestors (zoom/scale), which
  // turn `position: fixed` into ancestor-relative and push the menu away from
  // the cursor.
  return createPortal(
    <div
      ref={ref}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: "fixed",
        left: (clamped || position).x,
        top: (clamped || position).y,
        background: "white",
        color: "#222",
        border: "1px solid #ccc",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        padding: "4px 0",
        zIndex: 9999,
        minWidth: 180,
        fontSize: 14,
      }}
      role="menu"
    >
      {isBrandAsset && (
        <>
          <button
            type="button"
            role="menuitem"
            data-testid="brand-apply-all-scenes"
            style={menuItemStyle}
            onClick={() => {
              onClose && onClose();
              applyBrandAssetToAllScenes(dispatch, store.getState, { sourceBoxId: boxId });
            }}
          >
            {i18n.t("BrandPanel.apply_position_all") || "Tüm sahnelerde konumu güncelle"}
          </button>
          <div style={{ height: 1, background: "#eee", margin: "4px 0" }} />
        </>
      )}
      {textFont && (
        <>
          <button
            type="button"
            role="menuitem"
            data-testid="apply-font-all-scenes"
            style={menuItemStyle}
            onClick={() => {
              onClose && onClose();
              dispatch(batchSetSceneFont(textFont));
            }}
          >
            {i18n.t("BoxContextMenu.apply_font_all_scenes") || "Bu fontu tüm sahnelere uygula"}
          </button>
          <div style={{ height: 1, background: "#eee", margin: "4px 0" }} />
        </>
      )}
      {isInteractive && (
        <button
          type="button"
          role="menuitem"
          style={menuItemStyle}
          onClick={() => {
            onClose && onClose();
            dispatch(setInteractiveMode("design"));
            if (scenario && scenario.startSceneId) {
              dispatch(selectScene(boxId, scenario.startSceneId));
            }
          }}
        >
          {i18n.t("Edit") || "Düzenle"}
        </button>
      )}
      {canSmartElements && (
        <button
          type="button"
          role="menuitem"
          style={menuItemStyle}
          onClick={() => {
            onClose && onClose();
            dispatch(decomposeBoxWithAideaSmartElements(boxId));
          }}
        >
          {i18n.t("aideaSmartElements")}
        </button>
      )}
      {!canSmartElements && !isInteractive && (
        <button type="button" role="menuitem" disabled style={menuItemDisabledStyle}>
          {i18n.t("aideaSmartElements")}
        </button>
      )}
      {canReorder && (
        <>
          {(isInteractive || canSmartElements) && (
            <div style={{ height: 1, background: "#eee", margin: "4px 0" }} />
          )}
          <button
            type="button"
            role="menuitem"
            disabled={atFront}
            style={atFront ? menuItemDisabledStyle : menuItemStyle}
            onClick={() => reorder("front")}
          >
            {i18n.t("layerOrder.bringToFront") || "Öne Getir"}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={atFront}
            style={atFront ? menuItemDisabledStyle : menuItemStyle}
            onClick={() => reorder("ahead")}
          >
            {i18n.t("layerOrder.forward") || "İleri"}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={atBack}
            style={atBack ? menuItemDisabledStyle : menuItemStyle}
            onClick={() => reorder("behind")}
          >
            {i18n.t("layerOrder.backward") || "Geri"}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={atBack}
            style={atBack ? menuItemDisabledStyle : menuItemStyle}
            onClick={() => reorder("back")}
          >
            {i18n.t("layerOrder.sendToBack") || "Arkaya Gönder"}
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
